import React, { PureComponent } from 'react'
import Icon from '@mdi/react'
import IconButton from '@material-ui/core/IconButton'
import Tooltip from '@material-ui/core/Tooltip'
import Popover from '@material-ui/core/Popover'
import {
  mdiCompare,
  mdiImageFrame,
  mdiNumeric1BoxOutline,
  mdiArrowUp,
  mdiArrowDown,
  mdiArrowLeft,
  mdiArrowRight,
  mdiClose,
  mdiCloseCircleOutline,
  mdiCrosshairsGps,
  mdiInvertColors,
  mdiBrightness6,
  mdiContrastBox,
  mdiBlur,
  mdiChip,
  mdiVectorPolyline,
  mdiCheck,
  mdiHeartPulse,
  mdiMapMarkerPath,
  mdiDownload,
} from '@mdi/js'
import { DSA_SHIFT_STEP, DSA_SHIFT_STEP_COARSE } from '../utils/dsaSubtract'

const iconSize = '1.2rem'
const iconColor = '#FFFFFF'
const activeColor = '#7CFFB2'
const accent = '#5B9FD4'

const panelBg = '#1a1d21'
const cardBg = '#242830'
const cardBorder = 'rgba(255,255,255,0.08)'
const textMuted = 'rgba(255,255,255,0.62)'

/** Long-press para abrir el submenú (móvil y desktop). */
const LONG_PRESS_MS = 420

/** Acciones que mantienen el panel abierto */
const KEEP_OPEN = new Set([
  'invert-toggle',
  'gain+',
  'gain-',
  'offset+',
  'offset-',
  'noise+',
  'noise-',
  'pixel-shift-x+',
  'pixel-shift-x-',
  'pixel-shift-y+',
  'pixel-shift-y-',
  'pixel-shift-reset',
  'mask-current',
  'mask-first',
  'mask-auto-cardiac',
  'engine-auto',
  'engine-gpu',
  'engine-cpu',
  'vascular-synth',
  'synth-mode-minip',
  'synth-mode-mip',
  'synth-range-visible-end',
  'export-synth-png',
  'roadmap-toggle',
  'roadmap-alpha+',
  'roadmap-alpha-',
  'roadmap-lut-green',
  'roadmap-lut-red',
  'roadmap-lut-gray',
])

/**
 * Opciones legacy (ids) — compat con handleDsaMenuSelect.
 * @deprecated preferir cards del panel
 */
export const DSA_MENU_OPTIONS = [
  { id: 'toggle', label: 'Activar / desactivar DSA', icon: mdiCompare },
  { id: 'mask-current', label: 'Establecer como máscara', icon: mdiImageFrame },
  { id: 'mask-first', label: 'Máscara = frame 1', icon: mdiNumeric1BoxOutline },
  { id: 'invert-toggle', label: 'Invert LUT', icon: mdiInvertColors },
  { id: 'reset', label: 'Restablecer DSA', icon: mdiCloseCircleOutline },
]

function MiniBtn({ title, path, onClick, disabled, active }) {
  return (
    <Tooltip title={title}>
      <span>
        <IconButton
          size="small"
          disabled={disabled}
          onClick={onClick}
          aria-label={title}
          style={{
            color: active ? activeColor : '#fff',
            padding: 6,
            opacity: disabled ? 0.35 : 1,
            background: active ? 'rgba(124,255,178,0.12)' : 'rgba(255,255,255,0.06)',
            borderRadius: 8,
            margin: 2,
          }}
        >
          <Icon path={path} size="0.95rem" color="currentColor" />
        </IconButton>
      </span>
    </Tooltip>
  )
}

function DsaCard({
  title,
  body,
  icon,
  children,
  disabled,
  active,
  onClick,
}) {
  return (
    <div
      role={onClick ? 'button' : undefined}
      tabIndex={onClick && !disabled ? 0 : undefined}
      onClick={disabled ? undefined : onClick}
      onKeyDown={
        onClick && !disabled
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onClick(e)
              }
            }
          : undefined
      }
      style={{
        background: cardBg,
        border: `1px solid ${active ? 'rgba(124,255,178,0.45)' : cardBorder}`,
        borderRadius: 12,
        padding: '14px 14px 12px',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        minHeight: 132,
        cursor: onClick && !disabled ? 'pointer' : 'default',
        opacity: disabled ? 0.55 : 1,
        boxShadow: active ? '0 0 0 1px rgba(124,255,178,0.2)' : 'none',
        WebkitTapHighlightColor: 'transparent',
        userSelect: 'none',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 10,
            background: 'rgba(91,159,212,0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icon path={icon} size="1.15rem" color={accent} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: '0.04em',
              color: '#fff',
              lineHeight: 1.25,
              textTransform: 'uppercase',
            }}
          >
            {title}
          </div>
        </div>
        {active ? (
          <Icon path={mdiCheck} size="0.9rem" color={activeColor} />
        ) : null}
      </div>
      <div
        style={{
          fontSize: 11,
          lineHeight: 1.45,
          color: textMuted,
          flex: 1,
        }}
      >
        {body}
      </div>
      {children ? (
        <div
          style={{ marginTop: 4 }}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          {children}
        </div>
      ) : null}
    </div>
  )
}

/**
 * Panel DSA estilo cards (modelo clínico UDV).
 * Trigger: click = activar/desactivar; long-press / clic derecho = menú.
 * Mantiene hideTrigger / controlled para Edge long-press y Studio rail.
 */
class DsaMenu extends PureComponent {
  constructor(props) {
    super(props)
    this.state = { anchorEl: null }
    this._longPressTimer = null
    this._longPressFired = false
    this._pointerId = null
  }

  componentWillUnmount() {
    this.clearLongPress()
  }

  isControlled = () =>
    this.props.anchorEl !== undefined || this.props.open !== undefined

  clearLongPress = () => {
    if (this._longPressTimer) {
      clearTimeout(this._longPressTimer)
      this._longPressTimer = null
    }
  }

  openMenu = (anchor) => {
    if (!anchor) return
    if (this.isControlled()) {
      if (typeof this.props.onOpenRequest === 'function') {
        this.props.onOpenRequest(anchor)
      }
      return
    }
    this.setState({ anchorEl: anchor })
  }

  handleClose = () => {
    if (this.props.onClose) this.props.onClose()
    if (!this.isControlled()) this.setState({ anchorEl: null })
  }

  emit = (id, extra) => {
    if (!this.props.onSelect) return
    this.props.onSelect(id, extra || { id, label: id })
    if (!KEEP_OPEN.has(id) && id !== 'toggle') {
      this.handleClose()
    }
  }

  handlePointerDown = (e) => {
    if (e.button != null && e.button !== 0) return
    this._longPressFired = false
    this._pointerId = e.pointerId
    this.clearLongPress()
    const target = e.currentTarget
    this._longPressTimer = setTimeout(() => {
      this._longPressFired = true
      this.openMenu(target)
      if (typeof navigator !== 'undefined' && navigator.vibrate) {
        try {
          navigator.vibrate(12)
        } catch (err) {
          /* ignore */
        }
      }
    }, LONG_PRESS_MS)
  }

  handlePointerUp = (e) => {
    this.clearLongPress()
    if (this._pointerId != null && e.pointerId !== this._pointerId) return
    this._pointerId = null
  }

  handlePointerCancel = () => {
    this.clearLongPress()
    this._pointerId = null
  }

  handleClick = (e) => {
    // Tras long-press no togglear (el menú ya se abrió)
    if (this._longPressFired) {
      e.preventDefault()
      e.stopPropagation()
      this._longPressFired = false
      return
    }
    this.emit('toggle')
  }

  handleContextMenu = (e) => {
    // Desktop: clic derecho abre el submenú (y evita el menú nativo)
    e.preventDefault()
    e.stopPropagation()
    this.clearLongPress()
    this.openMenu(e.currentTarget)
  }

  renderSubmenuHint = () => (
    <span
      aria-hidden
      style={{
        position: 'absolute',
        right: 6,
        bottom: 6,
        display: 'flex',
        gap: 1.5,
        pointerEvents: 'none',
      }}
    >
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          style={{
            width: 2.5,
            height: 2.5,
            borderRadius: '50%',
            background: 'rgba(210, 210, 210, 0.95)',
            boxShadow: '0 0 1px rgba(0,0,0,0.6)',
          }}
        />
      ))}
    </span>
  )

  render() {
    const {
      hideTrigger,
      menuProps,
      enabled,
      maskMode,
      frameCount,
      pixelShift,
      invertDSA,
      gain,
      offset,
      noiseFloor,
      engineMode,
      synthMode,
      roadmap,
    } = this.props
    const controlled = this.isControlled()
    const anchorEl = controlled
      ? this.props.anchorEl || null
      : this.state.anchorEl
    const isOpen = controlled
      ? this.props.open !== undefined
        ? !!this.props.open
        : Boolean(anchorEl)
      : Boolean(anchorEl)

    const framesHint =
      frameCount > 1 ? `${frameCount} frames` : 'multi-frame'
    const sx = pixelShift && pixelShift.x != null ? Number(pixelShift.x) : 0
    const sy = pixelShift && pixelShift.y != null ? Number(pixelShift.y) : 0
    const g = gain != null ? Number(gain) : 1
    const nf = noiseFloor != null ? Number(noiseFloor) : 8
    const off = offset != null ? Number(offset) : null
    const inv = invertDSA === true
    const eng = engineMode || 'auto'
    const smode = synthMode === 'mip' ? 'mip' : 'minip'
    const rmOn = !!(roadmap && roadmap.on)
    const rmAlpha = roadmap && roadmap.alpha != null ? Number(roadmap.alpha) : 0.45
    const rmLut = (roadmap && roadmap.lut) || 'fluoro-green'
    const adjDisabled = !enabled

    const paperProps = (menuProps && menuProps.PaperProps) || {}
    const restMenuProps = { ...(menuProps || {}) }
    delete restMenuProps.PaperProps

    const tip = enabled
      ? `DSA activo · click desactiva · mantener / clic derecho = menú`
      : `DSA · click activa · mantener / clic derecho = menú`

    return (
      <div style={{ display: 'contents' }}>
        {!hideTrigger && (
          <Tooltip title={tip}>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flex: '0 0 auto',
                minWidth: 0,
              }}
            >
              <IconButton
                color="inherit"
                onClick={this.handleClick}
                onPointerDown={this.handlePointerDown}
                onPointerUp={this.handlePointerUp}
                onPointerLeave={this.handlePointerCancel}
                onPointerCancel={this.handlePointerCancel}
                onContextMenu={this.handleContextMenu}
                aria-label="DSA"
                aria-haspopup="menu"
                aria-expanded={isOpen ? 'true' : 'false'}
                className={this.props.triggerClassName || undefined}
                style={{
                  position: 'relative',
                  padding: 8,
                  minWidth: 44,
                  minHeight: 44,
                  margin: 0,
                  backgroundColor: isOpen
                    ? 'rgba(255, 255, 255, 0.1)'
                    : enabled
                      ? 'rgba(124, 255, 178, 0.08)'
                      : 'transparent',
                  ...(this.props.triggerStyle || {}),
                }}
              >
                <Icon
                  path={mdiCompare}
                  size={this.props.triggerIconSize || iconSize}
                  color={enabled || isOpen ? activeColor : iconColor}
                />
                {this.renderSubmenuHint()}
              </IconButton>
            </span>
          </Tooltip>
        )}

        <Popover
          anchorEl={anchorEl}
          open={isOpen}
          onClose={this.handleClose}
          getContentAnchorEl={null}
          anchorOrigin={
            hideTrigger
              ? { vertical: 'center', horizontal: 'left' }
              : { vertical: 'bottom', horizontal: 'center' }
          }
          transformOrigin={
            hideTrigger
              ? { vertical: 'center', horizontal: 'right' }
              : { vertical: 'top', horizontal: 'center' }
          }
          marginThreshold={12}
          PaperProps={{
            ...paperProps,
            style: {
              backgroundColor: panelBg,
              color: '#FFFFFF',
              width: 'min(560px, calc(100vw - 24px))',
              maxHeight: 'min(78vh, 640px)',
              overflow: 'auto',
              borderRadius: 16,
              border: '1px solid rgba(255,255,255,0.1)',
              boxShadow: '0 16px 48px rgba(0,0,0,0.55)',
              ...(paperProps.style || {}),
            },
          }}
          {...restMenuProps}
        >
          <div style={{ padding: '16px 16px 12px' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
                marginBottom: 14,
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    letterSpacing: '0.02em',
                    color: '#fff',
                  }}
                >
                  DSA — Optimización vascular
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: textMuted,
                    marginTop: 4,
                    lineHeight: 1.4,
                  }}
                >
                  {framesHint}
                  {enabled
                    ? ` · activo · Δ${sx},${sy} · G${g.toFixed(1)} · N${nf}${
                        off != null ? ` · O${off}` : ''
                      }${inv ? ' · INV' : ''}`
                    : ' · inactivo'}
                </div>
              </div>
              <MiniBtn
                title={enabled ? 'Desactivar DSA' : 'Activar DSA'}
                path={mdiCompare}
                active={!!enabled}
                onClick={() => this.emit('toggle')}
              />
              <MiniBtn
                title="Cerrar"
                path={mdiClose}
                onClick={this.handleClose}
              />
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: 10,
              }}
            >
              <DsaCard
                title="Pixel shift"
                icon={mdiCrosshairsGps}
                body={`Fino ±${DSA_SHIFT_STEP} px (flechas) · grueso ±${DSA_SHIFT_STEP_COARSE} (Shift+flechas) · R / clic central = reset.`}
                disabled={adjDisabled}
              >
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                  }}
                  onAuxClick={(e) => {
                    if (e.button === 1) {
                      e.preventDefault()
                      this.emit('pixel-shift-reset')
                    }
                  }}
                  onMouseDown={(e) => {
                    if (e.button === 1) e.preventDefault()
                  }}
                >
                  <MiniBtn
                    title={`↑ −${DSA_SHIFT_STEP}`}
                    path={mdiArrowUp}
                    disabled={adjDisabled}
                    onClick={() => this.emit('pixel-shift-y-')}
                  />
                  <MiniBtn
                    title={`↓ +${DSA_SHIFT_STEP}`}
                    path={mdiArrowDown}
                    disabled={adjDisabled}
                    onClick={() => this.emit('pixel-shift-y+')}
                  />
                  <MiniBtn
                    title={`← −${DSA_SHIFT_STEP}`}
                    path={mdiArrowLeft}
                    disabled={adjDisabled}
                    onClick={() => this.emit('pixel-shift-x-')}
                  />
                  <MiniBtn
                    title={`→ +${DSA_SHIFT_STEP}`}
                    path={mdiArrowRight}
                    disabled={adjDisabled}
                    onClick={() => this.emit('pixel-shift-x+')}
                  />
                  <MiniBtn
                    title="Centrar (0,0) — R / clic central"
                    path={mdiCrosshairsGps}
                    disabled={adjDisabled}
                    onClick={() => this.emit('pixel-shift-reset')}
                  />
                </div>
              </DsaCard>

              <DsaCard
                title="Invert LUT"
                icon={mdiInvertColors}
                body="OFF (defecto clínico): vasos oscuros. ON: invierte la LUT → vasos claros. Tecla I."
                active={enabled && inv}
                disabled={adjDisabled}
                onClick={() => this.emit('invert-toggle')}
              />

              <DsaCard
                title="Re-masking dinámico"
                icon={mdiImageFrame}
                body="Si la máscara tiene movimiento, elegí otro frame base (k_mask). Tecla M = frame visible."
                active={maskMode === 'current' || maskMode === 'first' || maskMode === 'auto-cardiac'}
                disabled={adjDisabled}
              >
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  <MiniBtn
                    title="Establecer frame actual como máscara (M)"
                    path={mdiImageFrame}
                    disabled={adjDisabled}
                    active={maskMode === 'current'}
                    onClick={() => this.emit('mask-current')}
                  />
                  <MiniBtn
                    title="Máscara = frame 1 (pre-contraste)"
                    path={mdiNumeric1BoxOutline}
                    disabled={adjDisabled}
                    active={maskMode === 'first'}
                    onClick={() => this.emit('mask-first')}
                  />
                  <MiniBtn
                    title="Auto-máscara cardíaca (ECG / ACF)"
                    path={mdiHeartPulse}
                    disabled={adjDisabled}
                    active={maskMode === 'auto-cardiac'}
                    onClick={() => this.emit('mask-auto-cardiac')}
                  />
                </div>
              </DsaCard>

              <DsaCard
                title="Ruido y ganancia"
                icon={mdiContrastBox}
                body="Gain / offset / noise floor post-sustracción. El WW/WC del viewer actúa sobre la diferencia."
                disabled={adjDisabled}
              >
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <MiniBtn
                    title="Ganancia +"
                    path={mdiContrastBox}
                    disabled={adjDisabled}
                    onClick={() => this.emit('gain+')}
                  />
                  <MiniBtn
                    title="Ganancia −"
                    path={mdiContrastBox}
                    disabled={adjDisabled}
                    onClick={() => this.emit('gain-')}
                  />
                  <MiniBtn
                    title="Offset +"
                    path={mdiBrightness6}
                    disabled={adjDisabled}
                    onClick={() => this.emit('offset+')}
                  />
                  <MiniBtn
                    title="Offset −"
                    path={mdiBrightness6}
                    disabled={adjDisabled}
                    onClick={() => this.emit('offset-')}
                  />
                  <MiniBtn
                    title="Noise floor +"
                    path={mdiBlur}
                    disabled={adjDisabled}
                    onClick={() => this.emit('noise+')}
                  />
                  <MiniBtn
                    title="Noise floor −"
                    path={mdiBlur}
                    disabled={adjDisabled}
                    onClick={() => this.emit('noise-')}
                  />
                </div>
              </DsaCard>

              <DsaCard
                title="Performance GPU"
                icon={mdiChip}
                body={`Motor ${eng.toUpperCase()}: CPU (estable) por defecto. Auto/GPU = WebGL2 con fallback si falla.`}
                active={eng === 'gpu' || eng === 'auto'}
                disabled={adjDisabled}
              >
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  <MiniBtn
                    title="Auto (GPU si disponible)"
                    path={mdiChip}
                    disabled={adjDisabled}
                    active={eng === 'auto'}
                    onClick={() => this.emit('engine-auto')}
                  />
                  <MiniBtn
                    title="Forzar GPU"
                    path={mdiChip}
                    disabled={adjDisabled}
                    active={eng === 'gpu'}
                    onClick={() => this.emit('engine-gpu')}
                  />
                  <MiniBtn
                    title="Forzar CPU"
                    path={mdiChip}
                    disabled={adjDisabled}
                    active={eng === 'cpu'}
                    onClick={() => this.emit('engine-cpu')}
                  />
                </div>
              </DsaCard>

              <DsaCard
                title="Síntesis vascular"
                icon={mdiVectorPolyline}
                body={`MinIP/MIP temporal del árbol vascular. Modo: ${smode.toUpperCase()}.`}
                disabled={adjDisabled}
              >
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  <MiniBtn
                    title="MinIP (vasos oscuros)"
                    path={mdiVectorPolyline}
                    disabled={adjDisabled}
                    active={smode === 'minip'}
                    onClick={() => this.emit('synth-mode-minip')}
                  />
                  <MiniBtn
                    title="MIP (vasos claros)"
                    path={mdiVectorPolyline}
                    disabled={adjDisabled}
                    active={smode === 'mip'}
                    onClick={() => this.emit('synth-mode-mip')}
                  />
                  <MiniBtn
                    title="Rango = visible→fin"
                    path={mdiImageFrame}
                    disabled={adjDisabled}
                    onClick={() => this.emit('synth-range-visible-end')}
                  />
                  <MiniBtn
                    title="Generar síntesis"
                    path={mdiCheck}
                    disabled={adjDisabled}
                    onClick={() => this.emit('vascular-synth')}
                  />
                  <MiniBtn
                    title="Exportar PNG"
                    path={mdiDownload}
                    disabled={adjDisabled}
                    onClick={() => this.emit('export-synth-png')}
                  />
                </div>
              </DsaCard>

              <DsaCard
                title="Roadmapping"
                icon={mdiMapMarkerPath}
                body={`Overlay de síntesis sobre nativo · α=${rmAlpha} · ${rmLut}`}
                active={rmOn}
                disabled={adjDisabled}
              >
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  <MiniBtn
                    title="Toggle roadmap"
                    path={mdiMapMarkerPath}
                    disabled={adjDisabled}
                    active={rmOn}
                    onClick={() => this.emit('roadmap-toggle')}
                  />
                  <MiniBtn
                    title="α +"
                    path={mdiBrightness6}
                    disabled={adjDisabled}
                    onClick={() => this.emit('roadmap-alpha+')}
                  />
                  <MiniBtn
                    title="α −"
                    path={mdiBrightness6}
                    disabled={adjDisabled}
                    onClick={() => this.emit('roadmap-alpha-')}
                  />
                  <MiniBtn
                    title="LUT verde flúor"
                    path={mdiCheck}
                    disabled={adjDisabled}
                    active={rmLut === 'fluoro-green'}
                    onClick={() => this.emit('roadmap-lut-green')}
                  />
                  <MiniBtn
                    title="LUT rojo"
                    path={mdiCheck}
                    disabled={adjDisabled}
                    active={rmLut === 'alert-red'}
                    onClick={() => this.emit('roadmap-lut-red')}
                  />
                  <MiniBtn
                    title="LUT gris"
                    path={mdiCheck}
                    disabled={adjDisabled}
                    active={rmLut === 'gray'}
                    onClick={() => this.emit('roadmap-lut-gray')}
                  />
                </div>
              </DsaCard>
            </div>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
                marginTop: 14,
                paddingTop: 10,
                borderTop: '1px solid rgba(255,255,255,0.08)',
              }}
            >
              <div style={{ fontSize: 10, color: textMuted, fontStyle: 'italic' }}>
                DSA 2.0 · GPU / síntesis / roadmap · D I M R · flechas
              </div>
              <MiniBtn
                title="Restablecer DSA"
                path={mdiCloseCircleOutline}
                onClick={() => this.emit('reset')}
              />
            </div>
          </div>
        </Popover>
      </div>
    )
  }
}

export default DsaMenu
